import { Box, Flex, Image, Text, IconButton } from '@chakra-ui/core'
import moment from 'moment'
import React from 'react'
import { BsFolderPlus } from 'react-icons/bs'
import { VscBell, VscHistory, VscWarning } from 'react-icons/vsc'
import { API_HOST } from '../../../constants'
import { BackButton, Card } from '../../../components'
import { PrimaryButton } from '../../../components/Buttons'
import { useAppContext } from '../../../context/AppProvider'
import { usePreviewerContext } from '../../../context/PreviewerProvider'
import {
  Enum_Notification_Type,
  EvidenceEntity,
  Maybe,
  NotificationEntity,
  TaskDefinitionEntity,
  TaskEntity,
  useCanEditTaskQuery,
  useGetNotificationQuery,
  useGetSingleBaoriderTaskQuery,
  useMarkNotificationAsReceivedMutation,
  UsersPermissionsUserEntity
} from '../../../generated/graphql'
import DataFields from '../../TaskReport/components/DataFields'
import { useAuthContext } from '../../../context/AuthProvider'
import { isNotificationRead } from '../utils'

type NotificationDetailsProps = {
  notificationId: string
  onBack: () => void
}

const NotificationDetails: React.FC<NotificationDetailsProps> = ({ notificationId, onBack }) => {
  const { data, loading } = useGetNotificationQuery({
    variables: { id: notificationId },
    fetchPolicy: 'network-only'
  })
  const { toggleDrawer2 } = useAppContext()
  const { setPreviewComponent } = usePreviewerContext()
  const [markNotificationAsReceived] = useMarkNotificationAsReceivedMutation()
  const notification = data?.getDataCollectorNotification
  const type = notification?.attributes?.type
  const action = notification?.attributes?.action
  const taskId = React.useMemo(() => {
    if (!action) return null
    const possiblePaths = [action?.task?.id, action?.data?.task?.id]
    return possiblePaths.find((id) => id) || null
  }, [action])

  const { data: canEditTask, loading: canEditTaskLoading } = useCanEditTaskQuery({
    variables: {
      id: taskId || ''
    },
    fetchPolicy: 'network-only'
  })
  const { data: taskData } = useGetSingleBaoriderTaskQuery({
    variables: { id: taskId || '' },
    skip: !taskId,
    fetchPolicy: 'network-only'
  })

  const { user } = useAuthContext()
  const task = taskData?.getSingleBaoriderTask

  const read = isNotificationRead(
    notification as NotificationEntity,
    user as UsersPermissionsUserEntity
  )

  return (
    <Card p={4} borderBottom="1px solid" borderColor="gray.200" bg="white">
      <BackButton onClick={onBack} title="Back to Notifications" />

      <Box mt={2}>
        {loading && <p>Loading...</p>}
        <Flex align="center" justify="space-between" mb={3}>
          <Flex align="center">
            <Flex
              color="green.700"
              borderRadius="full"
              width={8}
              height={8}
              minW={8}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <IconButton
                icon={
                  notification?.attributes?.type === Enum_Notification_Type.ReminderAlert
                    ? VscHistory
                    : notification?.attributes?.type === Enum_Notification_Type.EarlyWarning
                    ? VscWarning
                    : VscBell
                }
                aria-label="Notification Icon"
                size="sm"
                fontSize="md"
                variant="ghost"
              />
            </Flex>
            <Text fontSize="md" px={0} fontWeight="700" color="gray.900">
              {notification?.attributes?.title || 'Notification'}
            </Text>
          </Flex>
          <Text fontSize="xs" color="gray.500">
            {moment(notification?.attributes?.createdAt).format('ll')}
          </Text>
        </Flex>
        {notification?.attributes?.message ? (
          <Text fontSize="sm" pl={12} color="gray.700" lineHeight="tall" mb={3}>
            {notification?.attributes?.message}
          </Text>
        ) : null}

        {(() => {
          const rawUrl = (notification as any)?.attributes?.attachment?.data?.attributes?.url as
            | string
            | undefined
          const imgUrl = rawUrl
            ? rawUrl.startsWith('http')
              ? rawUrl
              : `${API_HOST}${rawUrl}`
            : undefined
          return imgUrl ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <Box
                position="relative"
                width="100%"
                maxW="520px"
                borderRadius="md"
                overflow="hidden"
              >
                <Box pt="56.25%" />
                <Image
                  src={imgUrl}
                  alt={notification?.attributes?.title || 'attachment'}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
              </Box>
            </Box>
          ) : null
        })()}

        <Box mt={4}>
          <DataFields task={task as Maybe<TaskEntity>} />
        </Box>

        <Box py={10}>
          {type === Enum_Notification_Type.MoreInfoRequested ? (
            task?.id && canEditTask?.canEditTask && !canEditTaskLoading ? (
              <PrimaryButton
                width="100%"
                onClick={() => {
                  toggleDrawer2(true)

                  setPreviewComponent?.({
                    title:
                      task?.attributes?.task_definition?.data?.attributes?.task_type?.data
                        ?.attributes?.title || '',
                    icon: BsFolderPlus,
                    content: (
                      <DataFields
                        task={task?.attributes?.task_definition?.data as TaskDefinitionEntity}
                        evidence={task?.attributes?.evidences?.data?.[0] as Maybe<EvidenceEntity>}
                        editedFrom={task?.id || ''}
                        key={`${task?.id}-${Date.now()}`}
                      />
                    )
                  })
                  onBack()
                }}
              >
                Edit Task
              </PrimaryButton>
            ) : (
              <Text textAlign="center" color="gray.500" fontSize="sm" mt={4}>
                No task available
              </Text>
            )
          ) : (type === Enum_Notification_Type.ReminderAlert ||
              type === Enum_Notification_Type.EarlyWarning) &&
            !read ? (
            <PrimaryButton
              onClick={async () => {
                const res = await markNotificationAsReceived({
                  variables: {
                    notificationId: notification?.id || ''
                  }
                })
                console.log(res)
                if (res.data?.markNotificationAsReceived?.id) {
                  onBack()
                }
              }}
              mx={4}
            >
              Received
            </PrimaryButton>
          ) : (
            <Text textAlign="center" color="gray.500" fontSize="sm" mt={4}>
              Notification already received
            </Text>
          )}
        </Box>
      </Box>
    </Card>
  )
}

export default NotificationDetails
